import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
// Updated: Show descriptions and images on flashcards (Feature #127)
// Updated: Show box number and colored line on flashcards (Feature #135)

// Box colors for Leitner system
const BOX_COLORS = {
  1: '#EF4444', // red
  2: '#F97316', // orange
  3: '#EAB308', // yellow
  4: '#84CC16', // yellowish green (lime)
  5: '#22C55E', // green
};

const getBoxColor = (box) => BOX_COLORS[box] || BOX_COLORS[1];

const TrainingSessionPage = () => {
  const { t } = useTranslation();
  const { listId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const direction = searchParams.get('direction') || '1to2';

  const [session, setSession] = useState(null);
  const [currentCard, setCurrentCard] = useState(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [cardsReviewed, setCardsReviewed] = useState(0);
  const [totalCards, setTotalCards] = useState(0);
  const [showBreakRecommendation, setShowBreakRecommendation] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sessionComplete, setSessionComplete] = useState(false);

  useEffect(() => {
    startSession();
  }, [listId]);

  const startSession = async () => {
    try {
      const response = await api.post('/training/start', {
        listId: parseInt(listId),
        direction: direction,
      });

      // Map backend response to frontend format
      const sessionData = {
        ...response.data,
        id: response.data.sessionId,
        vocabularies: response.data.cards || [],
      };
      setSession(sessionData);
      setTotalCards(sessionData.vocabularies?.length || 0);

      if (sessionData.vocabularies?.length > 0) {
        setCurrentCard(getNextCard(sessionData.vocabularies, direction));
      } else {
        setSessionComplete(true);
      }
    } catch (err) {
      setError(err.message || t('errors.serverError'));
    } finally {
      setIsLoading(false);
    }
  };

  const getNextCard = (vocabularies, dir) => {
    if (!vocabularies || vocabularies.length === 0) return null;

    const vocab = vocabularies[0];
    const cardDirection = dir === 'random'
      ? (Math.random() > 0.5 ? '1to2' : '2to1')
      : dir;

    return {
      vocabulary: vocab,
      question: cardDirection === '1to2' ? vocab.word1 : vocab.word2,
      answer: cardDirection === '1to2' ? vocab.word2 : vocab.word1,
      questionDescription: cardDirection === '1to2' ? vocab.description1 : vocab.description2,
      answerDescription: cardDirection === '1to2' ? vocab.description2 : vocab.description1,
      imageUrl: vocab.imageUrl,
      direction: cardDirection,
    };
  };

  // Feature #137: Allow flip back to question from answer (toggle flip)
  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleAnswer = async (isCorrect) => {
    if (isSubmitting || !currentCard) return;

    setIsSubmitting(true);

    try {
      await api.post('/training/answer', {
        sessionId: session.id,
        vocabularyId: currentCard.vocabulary.id,
        correct: isCorrect,
        direction: currentCard.direction,
      });

      const newCardsReviewed = cardsReviewed + 1;
      setCardsReviewed(newCardsReviewed);

      // Check for break recommendation
      if (newCardsReviewed === 50 && !showBreakRecommendation) {
        setShowBreakRecommendation(true);
        setIsSubmitting(false);
        return;
      }

      // Remove current card from list and get next
      const remainingVocabs = session.vocabularies.filter(
        (v) => v.id !== currentCard.vocabulary.id
      );

      if (remainingVocabs.length === 0) {
        await endSession();
      } else {
        setSession({ ...session, vocabularies: remainingVocabs });
        setCurrentCard(getNextCard(remainingVocabs, direction));
        setIsFlipped(false);
      }
    } catch (err) {
      setError(err.message || t('errors.serverError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const endSession = async () => {
    try {
      await api.post('/training/end', {
        sessionId: session.id,
      });
      setSessionComplete(true);
    } catch (err) {
      setError(err.message || t('errors.serverError'));
    }
  };

  const handleContinue = () => {
    setShowBreakRecommendation(false);
  };

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (sessionComplete) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </div>
        <h1>{t('training.sessionComplete')}</h1>
        <p style={{ color: 'var(--color-text-secondary)', margin: '1rem 0' }}>
          {cardsReviewed} {t('stats.cardsReviewed')}
        </p>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/lists')}
        >
          {t('common.back')}
        </button>
      </div>
    );
  }

  if (showBreakRecommendation) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        </div>
        <h2>{t('training.breakRecommendation')}</h2>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem' }}>
          <button className="btn btn-outline" onClick={endSession}>
            {t('training.finish')}
          </button>
          <button className="btn btn-primary" onClick={handleContinue}>
            {t('training.continueAnyway')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="form-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Progress */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span>{t('training.progress')}</span>
          <span>{cardsReviewed} {t('common.of')} {totalCards}</span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-bar-fill"
            style={{ width: `${(cardsReviewed / totalCards) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Flashcard */}
      {/* Feature #150: Added key prop to ensure React creates fresh DOM elements for each card */}
      {/* and removed onError handler that was incorrectly hiding images with valid URLs */}
      {currentCard && (
        <div className="flashcard-container" key={currentCard.vocabulary.id}>
          <div
            className={`flashcard ${isFlipped ? 'flipped' : ''}`}
            onClick={handleFlip}
            style={{ cursor: 'pointer' }}
          >
            <div className="flashcard-face flashcard-front">
              <div className={`flashcard-content ${currentCard.imageUrl ? 'has-image' : ''}`}>
                {currentCard.imageUrl && (
                  <img
                    src={currentCard.imageUrl}
                    alt=""
                    className="flashcard-image"
                  />
                )}
                <div className="flashcard-text-section">
                  <div className="flashcard-word">{currentCard.question}</div>
                  {currentCard.questionDescription && (
                    <div className="flashcard-description">{currentCard.questionDescription}</div>
                  )}
                </div>
                {/* Box number indicator */}
                <div className="flashcard-box-number" style={{ color: getBoxColor(currentCard.vocabulary.box) }}>
                  {currentCard.vocabulary.box}
                </div>
              </div>
              {/* Box color line at bottom */}
              <div
                className="flashcard-box-line"
                style={{ backgroundColor: getBoxColor(currentCard.vocabulary.box) }}
              />
            </div>
            <div className="flashcard-face flashcard-back">
              <div className={`flashcard-content ${currentCard.imageUrl ? 'has-image' : ''}`}>
                {currentCard.imageUrl && (
                  <img
                    src={currentCard.imageUrl}
                    alt=""
                    className="flashcard-image"
                  />
                )}
                <div className="flashcard-text-section">
                  <div className="flashcard-word">{currentCard.answer}</div>
                  {currentCard.answerDescription && (
                    <div className="flashcard-description">{currentCard.answerDescription}</div>
                  )}
                </div>
                {/* Box number indicator */}
                <div className="flashcard-box-number" style={{ color: getBoxColor(currentCard.vocabulary.box) }}>
                  {currentCard.vocabulary.box}
                </div>
              </div>
              {/* Box color line at bottom */}
              <div
                className="flashcard-box-line"
                style={{ backgroundColor: getBoxColor(currentCard.vocabulary.box) }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      {!isFlipped ? (
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button className="btn btn-primary" onClick={handleFlip}>
            {t('training.flip')}
          </button>
        </div>
      ) : (
        <>
          <div className="training-buttons">
            <button
              className="btn btn-error"
              onClick={() => handleAnswer(false)}
              disabled={isSubmitting}
            >
              {t('training.incorrect')}
            </button>
            <button
              className="btn btn-success"
              onClick={() => handleAnswer(true)}
              disabled={isSubmitting}
            >
              {t('training.correct')}
            </button>
          </div>
          {/* Feature #137: Flip back button */}
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button className="btn btn-outline" onClick={handleFlip}>
              {t('training.flipBack') || 'Zurück zur Frage'}
            </button>
          </div>
        </>
      )}

      {/* End session button */}
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <button className="btn btn-outline" onClick={endSession}>
          {t('training.finish')}
        </button>
      </div>
    </div>
  );
};

export default TrainingSessionPage;
